import { useState, useEffect, useRef } from "react";

import styles from "./ClubPhoto.module.scss";
import { Slider } from "@/components/Slider";
import { useAppDispatch, useAppSelector } from "@redux/store";
import { fetchGetGallery } from "@redux/gallery/asyncActions";
import { IGalleryCategory } from "@redux/gallery/types";

const CATEGORY_PRIORITY = [
	"Клуб",
	"Теннисные корты",
	"Padel-корты",
	"Фитнес и бассейн",
	"Кафе",
	"Зоны отдыха",
];

const sortGalleryByPriority = (gallery: IGalleryCategory[]): IGalleryCategory[] => {
	return [...gallery].sort((a, b) => {
		const aIndex = CATEGORY_PRIORITY.indexOf(a.title.trim());
		const bIndex = CATEGORY_PRIORITY.indexOf(b.title.trim());
		const aPriority = aIndex === -1 ? CATEGORY_PRIORITY.length : aIndex;
		const bPriority = bIndex === -1 ? CATEGORY_PRIORITY.length : bIndex;
		return aPriority - bPriority;
	});
};

export const ClubPhoto = () => {
	const dispatch = useAppDispatch();
	const { gallery, isLoading } = useAppSelector((state) => state.gallery);
	const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number | null>(null);
	const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
	const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
	const thumbnailsRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		dispatch(fetchGetGallery());
	}, [dispatch]);

	const sortedGallery = sortGalleryByPriority(gallery);

	const currentCategory = selectedCategoryIndex !== null ? sortedGallery[selectedCategoryIndex] : null;
	const sortedImgs = currentCategory ? currentCategory.img_urls.slice().sort((a, b) => a.id - b.id) : [];
	const currentImages = sortedImgs.map((img) => img.url);
	const thumbnailImages = sortedImgs.map((img) => img.url);

	useEffect(() => {
		setSelectedImageIndex(0);
	}, [selectedCategoryIndex]);

	useEffect(() => {
		if (thumbnailsRef.current) {
			const activeThumbnail = thumbnailsRef.current.children[selectedImageIndex] as HTMLElement;
			if (activeThumbnail) {
				activeThumbnail.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
			}
		}
	}, [selectedImageIndex]);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape" && fullscreenImage) {
				handleCloseFullscreen();
			}
		};
		if (fullscreenImage) {
			window.addEventListener("keydown", handleEscape);
		}
		return () => {
			document.body.style.overflow = "";
			window.removeEventListener("keydown", handleEscape);
		};
	}, [fullscreenImage]);

	const handleCategoryClick = (index: number) => {
		setSelectedCategoryIndex(index);
		setSelectedImageIndex(0);
	};

	const handleBack = () => {
		setSelectedCategoryIndex(null);
		setSelectedImageIndex(0);
	};

	const handleSlideChange = (index: number) => {
		setSelectedImageIndex(index);
	};

	const handleImageClick = (image: string) => {
		setFullscreenImage(image);
		document.body.style.overflow = "hidden";
	};

	const handleCloseFullscreen = () => {
		setFullscreenImage(null);
		document.body.style.overflow = "";
	};

	if (isLoading) {
		return (
			<div className={styles.loading}>Загрузка...</div>
		);
	}

	if (sortedGallery.length === 0) {
		return null;
	}

	// DETAIL VIEW
	if (selectedCategoryIndex !== null && currentCategory) {
		return (
			<>
				<div className={styles.detailWrapper}>
					<div className={styles.detailHeader}>
						<button className={styles.backButton} onClick={handleBack}>
							← Назад
						</button>
						<p className={styles.categoryTitle}>{currentCategory.title}</p>
					</div>
					<div className={styles.detailContent}>
						<Slider
							images={currentImages}
							selectedIndex={selectedImageIndex}
							onSlideChange={handleSlideChange}
							onImageClick={handleImageClick}
						/>
						<div className={styles.thumbnailsRow} ref={thumbnailsRef}>
							{thumbnailImages.map((image, index) => (
								<div
									key={index}
									className={`${styles.thumbnail} ${selectedImageIndex === index ? styles.thumbnailActive : ""}`}
									onClick={() => setSelectedImageIndex(index)}
								>
									<img src={image} alt={`Фото ${index + 1}`} loading="lazy" />
								</div>
							))}
						</div>
					</div>
				</div>

				{fullscreenImage && (
					<div className={styles.fullscreenOverlay} onClick={handleCloseFullscreen}>
						<button className={styles.closeButton} onClick={handleCloseFullscreen} aria-label="Закрыть">
							×
						</button>
						<img
							src={fullscreenImage}
							alt="Fullscreen"
							className={styles.fullscreenImage}
							onClick={(e) => e.stopPropagation()}
						/>
					</div>
				)}
			</>
		);
	}

	// LIST VIEW
	return (
		<div className={styles.categoriesGrid}>
			{sortedGallery.map((category, index) => {
				const previewImage = category.img_urls.length > 0
					? category.img_urls.slice().sort((a, b) => a.id - b.id)[0].url
					: null;

				return (
					<div
						key={category.title}
						className={styles.categoryCard}
						onClick={() => handleCategoryClick(index)}
					>
						<div className={styles.categoryCardImage}>
							{previewImage && <img src={previewImage} alt={category.title} loading="lazy" />}
						</div>
						<p className={styles.categoryCardTitle}>{category.title}</p>
					</div>
				);
			})}
		</div>
	);
};
